import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import "../styles/InformesConfirmados.css";
import { generateInformePDFMejorado } from "../modules/inspeccion/components/GenerateReportsPDFMejorado";
import { generateInformeDisciplinarioPDF } from "../modules/inspeccion/components/GenerateInformeDisciplinarioPDF";
import { generateSancionWord } from "../modules/administrador/components/GenerateSancionWord";
import { getMotivoConfig } from "../config/motivos.config";
import PerritoFeo from "../assets/perritofeo.png";
import CaballoFeliz from "../assets/caballofeliz.png";
import JoelChicha from "../assets/joelchicha.png";

// Sorpresa: solo el legajo 9066 ve estas imágenes flotando arriba de la pantalla
const LEGAJO_SORPRESAS = "9066";

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Devuelve la fecha de referencia según el estado actual
const getFechaReferenciaPorEstado = (inf) => {
  if (!inf) return null;

  switch (inf.Estado) {
    case "confirmado":
    case "devuelto":
      return inf.FechaConfirmacion || inf.FechaRealizado;

    case "impreso":
      return inf.FechaImpresion || inf.FechaConfirmacion || inf.FechaRealizado;

    case "con_descargo":
      return inf.FechaDescargo || inf.FechaImpresion || inf.FechaRealizado;

    case "con_propuesta":
      return (
        inf.FechaSancionPropuesta ||
        inf.FechaDescargo ||
        inf.FechaImpresion ||
        inf.FechaRealizado
      );

    case "finalizado":
      return (
        inf.FechaFinalizado ||
        inf.FechaSancionPropuesta ||
        inf.FechaDescargo ||
        inf.FechaRealizado
      );

    case "aprobado":
      return inf.FechaAprobado || inf.FechaRealizado;

    default:
      return inf.FechaRealizado || null;
  }
};

// ¿Pasaron más de 5 días desde la fecha de referencia?
const estaVencido = (inf) => {
  const fechaRefStr = getFechaReferenciaPorEstado(inf);
  if (!fechaRefStr) return false;

  const fechaRef = new Date(fechaRefStr);
  if (isNaN(fechaRef.getTime())) return false;

  const hoy = new Date();
  const diffMs = hoy - fechaRef;
  const diffDias = diffMs / (1000 * 60 * 60 * 24);

  return diffDias > 5;
};

const InformesRRHH = () => {
  const usuarioActual = JSON.parse(localStorage.getItem("user") || "null");
  const mostrarSorpresas = String(usuarioActual?.Legajo) === LEGAJO_SORPRESAS;

  const [informes, setInformes] = useState([]);
  const [selectedInforme, setSelectedInforme] = useState(null);
  const [incluirFinalizados, setIncluirFinalizados] = useState(false);
  const [filtroLegajo, setFiltroLegajo] = useState("");

  // Modales
  const [showDescargoModal, setShowDescargoModal] = useState(false);
  const [showPropuestaModal, setShowPropuestaModal] = useState(false);
  const [showAplicarModal, setShowAplicarModal] = useState(false);
  const [showDesestimarModal, setShowDesestimarModal] = useState(false);

  // Modal detalle
  const [showDetalle, setShowDetalle] = useState(false);
  const [detalleInforme, setDetalleInforme] = useState(null);

  // Campos controlados
  const [descargo, setDescargo] = useState("");
  const [sancionPropuesta, setSancionPropuesta] = useState("");
  const [observacionDesestimar, setObservacionDesestimar] = useState("");

  // Formulario estructurado de sanción aplicada (F041 Apercibimiento / F042 Suspensión)
  const sancionFormVacio = {
    Tipo: "apercibimiento",
    FaltaCaratulada: "",
    DiasSuspension: "",
    FechaDesde: "",
    FechaReintegro: "",
    FechaNotificacion: new Date().toISOString().slice(0, 10),
  };
  const [sancionForm, setSancionForm] = useState(sancionFormVacio);
  const [editandoSancion, setEditandoSancion] = useState(false);

  useEffect(() => {
    const fetchInformes = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/informes/rrhh`, {
          params: { incluirFinalizados },
          headers: getAuthHeaders()
        });
        setInformes(data);
      } catch (error) {
        console.error("❌ Error al obtener informes RRHH:", error);
      }
    };
    fetchInformes();
  }, [incluirFinalizados]);

  // Solo el motivo "Generales" usa el header F073 (Informes Disciplinarios);
  // el resto de los motivos, incluido "No Cumple Tareas en Horario Laboral", sigue con el F039.
  const esInformeDisciplinario = (motivo) => getMotivoConfig(motivo)?.pdfTemplate === 'F073';

  const generarPDFInforme = (informe) => {
    if (esInformeDisciplinario(informe.Motivo)) {
      return generateInformeDisciplinarioPDF({
        informeNumero: informe.NroInforme,
        lugarControl: informe.LugarControl,
        fecha: informe.FechaRealizado,
        hora: informe.Hora || "",
        motivoDetalle: informe.MotivoDetalle,
        empleado: informe.NombreEmp || informe.LegajoEmp,
        legajo: informe.LegajoEmp,
        firmaEmpleadoBase64: informe.FirmaBase64 || ""
      });
    }

    return generateInformePDFMejorado({
      informeNumero: informe.NroInforme,
      lugarControl: informe.LugarControl,
      fecha: informe.FechaRealizado,
      hora: informe.Hora || "",
      actuante: informe.NombreInformante || informe.RealizadoPor,
      legajoActuante: informe.RealizadoPor || "",
      motivo: informe.Motivo,
      motivoDetalle: informe.MotivoDetalle,
      empleado: informe.NombreEmp || informe.LegajoEmp,
      legajo: informe.LegajoEmp,
      nroCoche: informe.CocheInterno,
      linea: informe.Linea,
      servicio: informe.Servicio,
      horaNotificacion: informe.HoraNotificacion,
      descargoTexto: informe.Descargo || "",
      fechaRespuesta: informe.FechaDescargo,
      firmaEmpleadoBase64: informe.FirmaBase64 || "",
      firmaInspectorBase64: informe.FirmaInspector || ""
    });
  };

  // Imprimir informe
  const imprimirInforme = async (informe) => {
    try {
      await generarPDFInforme(informe);

      // Solo cambiar estado a "impreso" si el informe está "confirmado"
      // Si ya está finalizado, solo generamos el PDF sin cambiar estado
      if (informe.Estado === "confirmado") {
        await axios.put(
          `${API_URL}/api/informes/${informe.id}/imprimir`,
          {},
          {
            headers: getAuthHeaders()
          }
        );

        setInformes((prev) =>
          prev.map((inf) =>
            inf.id === informe.id ? { ...inf, Estado: "impreso" } : inf
          )
        );
      }
    } catch (error) {
      console.error("❌ Error al imprimir informe:", error);
    }
  };

  // Generar PDF sin cambiar estado (solo para visualización)
  const generarPDFSinCambiarEstado = async (informe) => {
    try {
      await generarPDFInforme(informe);
      // NO cambia el estado del informe
    } catch (error) {
      console.error("❌ Error al generar PDF:", error);
    }
  };

  // Cargar descargo
  const cargarDescargo = async (id, texto) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/descargo`,
        { Descargo: texto },  // ✅ Usar el parámetro 'texto' en lugar del estado
        {
          headers: getAuthHeaders()
        }
      );
      setInformes((prev) =>
        prev.map((inf) =>
          inf.id === id
            ? { ...inf, Estado: "con_descargo", Descargo: texto }
            : inf
        )
      );
      setShowDescargoModal(false);
      setDescargo("");
    } catch (error) {
      console.error("❌ Error al cargar descargo:", error);
      alert(`Error al cargar descargo: ${error.response?.data?.error || error.message}`);
    }
  };

  // Cargar sanción propuesta
  const cargarPropuesta = async (id, sancion) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/sancion-propuesta`,
        { SancionPropuesta: sancion },
        {
          headers: getAuthHeaders()
        }
      );
      setInformes((prev) =>
        prev.map((inf) =>
          inf.id === id
            ? { ...inf, Estado: "con_propuesta", SancionPropuesta: sancion }
            : inf
        )
      );
      setShowPropuestaModal(false);
      setSancionPropuesta("");
    } catch (error) {
      console.error("❌ Error al cargar sanción propuesta:", error);
    }
  };

  // Registrar o editar la sanción aplicada (F041/F042)
  const aplicarSancion = async (id, datos) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/sancion-aplicada`,
        datos,
        {
          headers: getAuthHeaders()
        }
      );
      setInformes((prev) =>
        prev.map((inf) =>
          inf.id === id
            ? {
                ...inf,
                Estado: "finalizado",
                SancionTipo: datos.Tipo,
                SancionFaltaCaratulada: datos.FaltaCaratulada,
                SancionDiasSuspension: datos.DiasSuspension || null,
                SancionFechaDesde: datos.FechaDesde || null,
                SancionFechaReintegro: datos.FechaReintegro || null,
                SancionFechaNotificacion: datos.FechaNotificacion || null,
              }
            : inf
        )
      );
      setShowAplicarModal(false);
      setSancionForm(sancionFormVacio);
      setEditandoSancion(false);
    } catch (error) {
      console.error("❌ Error al aplicar sanción:", error);
      alert(`Error al guardar la sanción: ${error.response?.data?.error || error.message}`);
    }
  };

  // Calcula una fecha de reintegro sugerida (días corridos desde la fecha de inicio), editable por el usuario
  const calcularFechaReintegroSugerida = (fechaDesde, dias) => {
    if (!fechaDesde || !dias) return "";
    const [y, m, d] = fechaDesde.split("-").map(Number);
    const fecha = new Date(y, m - 1, d);
    fecha.setDate(fecha.getDate() + parseInt(dias, 10));
    return fecha.toISOString().slice(0, 10);
  };

  const actualizarCampoSancion = (campo, valor) => {
    setSancionForm((prev) => {
      const next = { ...prev, [campo]: valor };
      if (campo === "DiasSuspension" || campo === "FechaDesde") {
        next.FechaReintegro = calcularFechaReintegroSugerida(
          campo === "FechaDesde" ? valor : prev.FechaDesde,
          campo === "DiasSuspension" ? valor : prev.DiasSuspension
        ) || next.FechaReintegro;
      }
      return next;
    });
  };

  const sancionFormValido =
    sancionForm.FaltaCaratulada.trim() &&
    (sancionForm.Tipo === "apercibimiento" ||
      (sancionForm.DiasSuspension && sancionForm.FechaDesde && sancionForm.FechaReintegro));

  // Genera y descarga el Word de notificación de sanción (F041/F042) de un informe finalizado
  const imprimirSancion = async (informe) => {
    try {
      await generateSancionWord({
        tipo: informe.SancionTipo || "apercibimiento",
        informeNumero: informe.NroInforme,
        fechaInforme: informe.FechaRealizado,
        empleado: informe.NombreEmp || informe.LegajoEmp,
        legajo: informe.LegajoEmp,
        faltaCaratulada: informe.SancionFaltaCaratulada,
        diasSuspension: informe.SancionDiasSuspension,
        fechaDesde: informe.SancionFechaDesde,
        fechaReintegro: informe.SancionFechaReintegro,
        fechaNotificacion: informe.SancionFechaNotificacion,
      });
    } catch (error) {
      console.error("❌ Error al generar el Word de la sanción:", error);
      alert("Error al generar el documento de la sanción");
    }
  };

  // Abrir modales core
  const abrirModalDescargo = (inf) => {
    setSelectedInforme(inf);
    setShowDescargoModal(true);
  };
  const abrirModalPropuesta = (inf) => {
    setSelectedInforme(inf);
    setShowPropuestaModal(true);
  };
  const abrirModalAplicar = (inf) => {
    setSelectedInforme(inf);
    const yaTieneSancion = !!inf.SancionTipo;
    setEditandoSancion(yaTieneSancion);
    setSancionForm(
      yaTieneSancion
        ? {
            Tipo: inf.SancionTipo,
            FaltaCaratulada: inf.SancionFaltaCaratulada || "",
            DiasSuspension: inf.SancionDiasSuspension || "",
            FechaDesde: inf.SancionFechaDesde ? String(inf.SancionFechaDesde).slice(0, 10) : "",
            FechaReintegro: inf.SancionFechaReintegro ? String(inf.SancionFechaReintegro).slice(0, 10) : "",
            FechaNotificacion: inf.SancionFechaNotificacion
              ? String(inf.SancionFechaNotificacion).slice(0, 10)
              : new Date().toISOString().slice(0, 10),
          }
        : { ...sancionFormVacio, FaltaCaratulada: inf.MotivoDetalle || "" }
    );
    setShowAplicarModal(true);
  };

  // Abrir detalle
  const abrirDetalle = (inf) => {
    setDetalleInforme(inf);
    setShowDetalle(true);
  };

  // Desestimar informe desde RRHH
  const desestimarInforme = async (id, observacion) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/confirmar`,
        {
          Estado: "desestimado",
          Observacion: observacion,
        },
        {
          headers: getAuthHeaders()
        }
      );
      // Remover de la lista
      setInformes((prev) => prev.filter((inf) => inf.id !== id));
      setShowDesestimarModal(false);
      setObservacionDesestimar("");
      setSelectedInforme(null);
      alert("Informe desestimado correctamente");
    } catch (error) {
      console.error("❌ Error al desestimar informe:", error);
      alert(`Error al desestimar: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    }
  };

  const abrirModalDesestimar = (inf) => {
    setSelectedInforme(inf);
    setObservacionDesestimar("");
    setShowDesestimarModal(true);
  };

  const informesFiltrados = informes.filter((inf) =>
    !filtroLegajo.trim() ||
    String(inf.LegajoEmp ?? "").toLowerCase().includes(filtroLegajo.trim().toLowerCase())
  );

  return (
    <div className="informes-container">
      <Navbar />
      <h2 className="title">Informes RRHH</h2>

      <div className="mb-3">
        <label className="form-check-label">
          <input
            type="checkbox"
            className="form-check-input me-2"
            checked={incluirFinalizados}
            onChange={(e) => setIncluirFinalizados(e.target.checked)}
          />
          Mostrar informes finalizados
        </label>
      </div>

      <div className="mb-3">
        <label htmlFor="filtro-legajo" style={{ fontSize: 14, fontWeight: 500, marginRight: 8 }}>
          Filtrar por legajo informado
        </label>
        <input
          id="filtro-legajo"
          type="text"
          value={filtroLegajo}
          onChange={(e) => setFiltroLegajo(e.target.value)}
          placeholder="Ej: 1234"
          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" }}
        />
      </div>

      <div className="table-wrapper">
        <table className="informes-table">
          <thead>
            <tr>
              <th>N° Informe</th>
              <th>Inspector</th>
              <th>Empleado</th>
              <th>Legajo</th>
              <th>Motivo</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {informesFiltrados.map((inf) => {
              const vencido = estaVencido(inf);
              const fechaRef = getFechaReferenciaPorEstado(inf);

              return (
                <tr
                  key={inf.id}
                  className={vencido ? "fila-vencida" : ""}
                  title={
                    vencido && fechaRef
                      ? `Más de 5 días desde: ${new Date(
                          fechaRef
                        ).toLocaleString("es-AR")}`
                      : ""
                  }
                >
                  <td>{inf.NroInforme}</td>
                  <td>{inf.NombreInformante || 'Desconocido'}</td>
                  <td>{inf.NombreEmp || '-'}</td>
                  <td>{inf.LegajoEmp || '-'}</td>
                  <td>{inf.Motivo}</td>
                  <td>
                    {inf.FechaRealizado
                      ? inf.FechaRealizado.split("T")[0]
                      : ""}
                  </td>
                  <td>
                    <span
                      className={
                        vencido ? "estado-label estado-vencido" : "estado-label"
                      }
                    >
                      {inf.Estado}
                      {vencido && <span className="estado-alerta"> ⚠</span>}
                    </span>
                  </td>
                  <td className="acciones">
                    <button
                      className="btn-detalle"
                      onClick={() => abrirDetalle(inf)}
                    >
                      📄 Detalle
                    </button>

                    {/* Botón Ver PDF - Siempre visible, no cambia estado */}
                    <button
                      className="btn-ver-pdf"
                      onClick={() => generarPDFSinCambiarEstado(inf)}
                      title="Ver/Imprimir PDF (sin cambiar estado)"
                    >
                      🖨️ Ver PDF
                    </button>

                    {/* Botón Desestimar - Solo si NO está finalizado */}
                    {inf.Estado !== "finalizado" && (
                      <button
                        className="btn-desestimar"
                        onClick={() => abrirModalDesestimar(inf)}
                        title="Desestimar informe"
                      >
                        ❌ Desestimar
                      </button>
                    )}

                    {inf.Estado === "confirmado" && (
                      <button
                        className="btn-imprimir"
                        onClick={() => imprimirInforme(inf)}
                      >
                        🖨 Imprimir
                      </button>
                    )}

                    {inf.Estado === "impreso" && (
                      <button
                        className="btn-descargo"
                        onClick={() => abrirModalDescargo(inf)}
                      >
                        Cargar descargo
                      </button>
                    )}

                    {inf.Estado === "con_descargo" && (
                      <button
                        className="btn-propuesta"
                        onClick={() => abrirModalPropuesta(inf)}
                      >
                        Proponer sanción
                      </button>
                    )}

                    {inf.Estado === "con_propuesta" && (
                      <button
                        className="btn-aplicar"
                        onClick={() => abrirModalAplicar(inf)}
                      >
                        Registrar sanción
                      </button>
                    )}

                    {inf.Estado === "finalizado" && (
                      <>
                        <button
                          className="btn-imprimir"
                          onClick={() => imprimirInforme(inf)}
                          title="Imprimir informe (F039)"
                        >
                          🖨 Informe
                        </button>
                        {inf.SancionTipo && (
                          <button
                            className="btn-imprimir"
                            onClick={() => imprimirSancion(inf)}
                            title={`Descargar notificación de sanción en Word (${inf.SancionTipo === "apercibimiento" ? "F041" : "F042"})`}
                          >
                            💾 Sanción (Word)
                          </button>
                        )}
                        <button
                          className="btn-propuesta"
                          onClick={() => abrirModalAplicar(inf)}
                          title="Editar los datos de la sanción cargada"
                        >
                          ✏️ Editar sanción
                        </button>
                        <span className="badge-finalizado">✔ Finalizado</span>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal Descargo */}
      {showDescargoModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowDescargoModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Cargar descargo para informe #{selectedInforme.NroInforme}
            </h3>
            <p>
              <strong>Motivo:</strong> {selectedInforme.Motivo}
            </p>
            <textarea
              value={descargo}
              onChange={(e) => setDescargo(e.target.value)}
              rows={4}
              style={{ width: "100%" }}
              placeholder="Escriba el descargo del empleado..."
            />
            <div className="modal-footer">
              <button
                onClick={() => setShowDescargoModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() => cargarDescargo(selectedInforme.id, descargo)}
                className="btn btn-primary"
                disabled={!descargo.trim()}
              >
                Guardar descargo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Propuesta */}
      {showPropuestaModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowPropuestaModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Proponer sanción para informe #{selectedInforme.NroInforme}
            </h3>
            <p>
              <strong>Motivo:</strong> {selectedInforme.Motivo}
            </p>
            {selectedInforme.Descargo && (
              <p>
                <strong>Descargo:</strong> {selectedInforme.Descargo}
              </p>
            )}
            <textarea
              value={sancionPropuesta}
              onChange={(e) => setSancionPropuesta(e.target.value)}
              rows={4}
              style={{ width: "100%" }}
              placeholder="Escriba la sanción propuesta..."
            />
            <div className="modal-footer">
              <button
                onClick={() => setShowPropuestaModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() =>
                  cargarPropuesta(selectedInforme.id, sancionPropuesta)
                }
                className="btn btn-primary"
                disabled={!sancionPropuesta.trim()}
              >
                Guardar propuesta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aplicar/Editar sanción */}
      {showAplicarModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowAplicarModal(false)}
        >
          <div className="modal-content modal-content-sancion" onClick={(e) => e.stopPropagation()}>
            <h3>
              {editandoSancion ? "Editar sanción" : "Registrar sanción"} — Informe #{selectedInforme.NroInforme}
            </h3>
            <div className="sancion-form">
              <p>
                <strong>Motivo:</strong> {selectedInforme.Motivo}
              </p>
              {selectedInforme.Descargo && (
                <p>
                  <strong>Descargo:</strong> {selectedInforme.Descargo}
                </p>
              )}
              {selectedInforme.SancionPropuesta && (
                <p>
                  <strong>Sanción propuesta:</strong>{" "}
                  {selectedInforme.SancionPropuesta}
                </p>
              )}

              <div className="form-group">
                <label>Tipo de sanción</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="tipoSancion"
                      checked={sancionForm.Tipo === "apercibimiento"}
                      onChange={() => actualizarCampoSancion("Tipo", "apercibimiento")}
                    />
                    Apercibimiento (F041)
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="tipoSancion"
                      checked={sancionForm.Tipo === "suspension"}
                      onChange={() => actualizarCampoSancion("Tipo", "suspension")}
                    />
                    Suspensión (F042)
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>
                  {sancionForm.Tipo === "apercibimiento"
                    ? 'Falta caratulada como ("...")'
                    : "Falta laboral (consistente en...)"}
                </label>
                <textarea
                  value={sancionForm.FaltaCaratulada}
                  onChange={(e) => actualizarCampoSancion("FaltaCaratulada", e.target.value)}
                  rows={3}
                  style={{ width: "100%" }}
                  placeholder="Describa brevemente la falta que motiva la sanción..."
                />
              </div>

              {sancionForm.Tipo === "suspension" && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Días de suspensión</label>
                    <input
                      type="number"
                      min="1"
                      value={sancionForm.DiasSuspension}
                      onChange={(e) => actualizarCampoSancion("DiasSuspension", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Suspendido desde</label>
                    <input
                      type="date"
                      value={sancionForm.FechaDesde}
                      onChange={(e) => actualizarCampoSancion("FechaDesde", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Reintegro (sugerido, editable)</label>
                    <input
                      type="date"
                      value={sancionForm.FechaReintegro}
                      onChange={(e) => actualizarCampoSancion("FechaReintegro", e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Fecha de notificación</label>
                <input
                  type="date"
                  value={sancionForm.FechaNotificacion}
                  onChange={(e) => actualizarCampoSancion("FechaNotificacion", e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowAplicarModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() => aplicarSancion(selectedInforme.id, sancionForm)}
                className="btn btn-primary"
                disabled={!sancionFormValido}
              >
                {editandoSancion ? "Guardar cambios" : "Guardar sanción aplicada"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Desestimar */}
      {showDesestimarModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowDesestimarModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#dc3545' }}>
              ⚠️ Desestimar informe #{selectedInforme.NroInforme}
            </h3>
            <p>
              <strong>Inspector:</strong> {selectedInforme.NombreInformante}
            </p>
            <p>
              <strong>Empleado:</strong> {selectedInforme.NombreEmp} ({selectedInforme.LegajoEmp})
            </p>
            <p>
              <strong>Motivo:</strong> {selectedInforme.Motivo}
            </p>
            <p style={{ color: '#666', fontSize: '14px', marginTop: '15px' }}>
              <strong>Importante:</strong> Al desestimar este informe, se archivará y no continuará el proceso disciplinario.
            </p>
            <textarea
              value={observacionDesestimar}
              onChange={(e) => setObservacionDesestimar(e.target.value)}
              rows={4}
              style={{ width: "100%", marginTop: '10px' }}
              placeholder="Ingrese el motivo por el cual desestima este informe (obligatorio)..."
            />
            <div className="modal-footer">
              <button
                onClick={() => setShowDesestimarModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() => desestimarInforme(selectedInforme.id, observacionDesestimar)}
                className="btn btn-danger"
                disabled={!observacionDesestimar.trim()}
                style={{ backgroundColor: '#dc3545' }}
              >
                Confirmar desestimación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle */}
      {showDetalle && detalleInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowDetalle(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Detalle del Informe #{detalleInforme.NroInforme}</h3>

            <div className="detalle-grid">
              <p>
                <strong>Inspector</strong>
                {detalleInforme.NombreInformante || 'Desconocido'}
              </p>
              <p>
                <strong>Empleado</strong>
                {detalleInforme.NombreEmp} ({detalleInforme.LegajoEmp})
              </p>
              <p>
                <strong>Fecha</strong>
                {detalleInforme.FechaRealizado
                  ? detalleInforme.FechaRealizado.split("T")[0]
                  : "-"}
              </p>
              <p>
                <strong>Hora</strong>
                {detalleInforme.Hora || "-"}
              </p>
              <p>
                <strong>Lugar de Control</strong>
                {detalleInforme.LugarControl || "-"}
              </p>
              <p>
                <strong>Servicio</strong>
                {detalleInforme.Servicio || "-"}
              </p>
              <p>
                <strong>Línea</strong>
                {detalleInforme.Linea || "-"}
              </p>
              <p>
                <strong>Coche Interno</strong>
                {detalleInforme.CocheInterno || "-"}
              </p>
              <p style={{ gridColumn: '1 / -1' }}>
                <strong>Motivo</strong>
                {detalleInforme.Motivo || "-"}
              </p>
              <p style={{ gridColumn: '1 / -1' }}>
                <strong>Detalle del Motivo</strong>
                {detalleInforme.MotivoDetalle || "-"}
              </p>

              {detalleInforme.Descargo && (
                <p style={{ gridColumn: '1 / -1' }}>
                  <strong>Descargo del Empleado</strong>
                  {detalleInforme.Descargo}
                </p>
              )}
              {detalleInforme.SancionPropuesta && (
                <p style={{ gridColumn: '1 / -1' }}>
                  <strong>Sanción Propuesta</strong>
                  {detalleInforme.SancionPropuesta}
                </p>
              )}
              {detalleInforme.SancionTipo && (
                <>
                  <p>
                    <strong>Tipo de Sanción</strong>
                    {detalleInforme.SancionTipo === "apercibimiento" ? "Apercibimiento (F041)" : "Suspensión (F042)"}
                  </p>
                  <p style={{ gridColumn: '1 / -1' }}>
                    <strong>Falta Caratulada Como</strong>
                    {detalleInforme.SancionFaltaCaratulada || "-"}
                  </p>
                  {detalleInforme.SancionTipo === "suspension" && (
                    <>
                      <p>
                        <strong>Días de Suspensión</strong>
                        {detalleInforme.SancionDiasSuspension || "-"}
                      </p>
                      <p>
                        <strong>Suspendido Desde</strong>
                        {detalleInforme.SancionFechaDesde ? String(detalleInforme.SancionFechaDesde).slice(0, 10) : "-"}
                      </p>
                      <p>
                        <strong>Reintegro</strong>
                        {detalleInforme.SancionFechaReintegro ? String(detalleInforme.SancionFechaReintegro).slice(0, 10) : "-"}
                      </p>
                    </>
                  )}
                  {detalleInforme.SancionModificadoPor ? (
                    <p style={{ gridColumn: '1 / -1' }}>
                      <strong>Última Edición</strong>
                      {detalleInforme.SancionModificadoPor} — {detalleInforme.SancionFechaModificacion ? new Date(detalleInforme.SancionFechaModificacion).toLocaleString("es-AR") : "-"}
                    </p>
                  ) : (
                    <p style={{ gridColumn: '1 / -1' }}>
                      <strong>Cargada Por</strong>
                      {detalleInforme.SancionCreadoPor} — {detalleInforme.SancionFechaCreacion ? new Date(detalleInforme.SancionFechaCreacion).toLocaleString("es-AR") : "-"}
                    </p>
                  )}
                </>
              )}
              
              {/* Imagen adjunta si existe */}
              {detalleInforme.ImagenAdjunta && (
                <div className="detalle-imagen-container">
                  <div className="detalle-imagen-header">
                    <p>📷 Imagen adjunta</p>
                    <button 
                      className="btn-abrir-imagen"
                      onClick={() => window.open(`${API_URL}${detalleInforme.ImagenAdjunta}`, '_blank')}
                    >
                      🔗 Abrir en nueva pestaña
                    </button>
                  </div>
                  <div 
                    className="detalle-imagen-wrapper"
                    onClick={() => window.open(`${API_URL}${detalleInforme.ImagenAdjunta}`, '_blank')}
                    title="Click para abrir en tamaño completo"
                  >
                    <img 
                      src={`${API_URL}${detalleInforme.ImagenAdjunta}`}
                      alt="Imagen del informe"
                    />
                    <div className="imagen-overlay-hint">
                      🔍 Click para ampliar
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-cerrar"
                onClick={() => setShowDetalle(false)}
              >
                ✕ Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarSorpresas && (
        <>
          <img src={PerritoFeo} alt="" className="sorpresa-flotante sorpresa-izquierda" />
          <img src={CaballoFeliz} alt="" className="sorpresa-flotante sorpresa-centro" />
          <img src={JoelChicha} alt="" className="sorpresa-flotante sorpresa-derecha" />
        </>
      )}
    </div>
  );
};

export default InformesRRHH;
